'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface PricingCalculatorProps {
  className?: string;
}

const PricingCalculator = ({ className = '' }: PricingCalculatorProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [weight, setWeight] = useState<number>(5);
  const [serviceType, setServiceType] = useState<string>('standard');
  const [addOns, setAddOns] = useState<string[]>([]);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  if (!isHydrated) {
    return (
      <div className={`bg-card rounded-xl shadow-lg p-6 md:p-8 border border-border ${className}`}>
        <div className="animate-pulse">
          <div className="h-8 bg-muted rounded w-3/4 mb-6"></div>
          <div className="space-y-4">
            <div className="h-12 bg-muted rounded"></div>
            <div className="h-12 bg-muted rounded"></div>
            <div className="h-24 bg-muted rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  const servicePrices: { [key: string]: number } = {
    standard: 8.0,
    express: 12.0,
    premium: 15.0,
    delicate: 18.0,
  };

  const addOnPrices: { [key: string]: number } = {
    ironing: 3.0,
    folding: 2.0,
    delivery: 5.0,
    stainRemoval: 4.0,
  };

  const calculateTotal = (): number => {
    let total = weight * servicePrices[serviceType];
    addOns.forEach((addOn) => {
      total += weight * addOnPrices[addOn];
    });
    return total;
  };

  const handleAddOnToggle = (addOn: string) => {
    setAddOns((prev) =>
      prev.includes(addOn) ? prev.filter((item) => item !== addOn) : [...prev, addOn]
    );
  };

  return (
    <div className={`bg-card rounded-xl shadow-lg p-6 md:p-8 border border-border ${className}`}>
      <div className="flex items-center space-x-3 mb-6">
        <div className="bg-gradient-to-br from-primary to-secondary rounded-lg p-2">
          <Icon name="CalculatorIcon" size={24} variant="outline" className="text-white" />
        </div>
        <h3 className="text-2xl font-heading font-bold text-textPrimary">Kalkulator Harga</h3>
      </div>

      <div className="space-y-6">
        <div>
          <label className="block text-sm font-body font-semibold text-textPrimary mb-3">
            Berat Pakaian (kg)
          </label>
          <div className="flex items-center space-x-4">
            <input
              type="range"
              min="1"
              max="20"
              value={weight}
              onChange={(e) => setWeight(Number(e.target.value))}
              className="flex-1 h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
            />
            <div className="bg-gradient-to-r from-primary to-secondary text-white rounded-lg px-4 py-2 min-w-[80px] text-center font-heading font-bold">
              {weight} kg
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-body font-semibold text-textPrimary mb-3">
            Jenis Servis
          </label>
          <div className="grid grid-cols-2 gap-3">
            {Object.entries(servicePrices).map(([type, price]) => (
              <button
                key={type}
                onClick={() => setServiceType(type)}
                className={`p-4 rounded-lg border-2 transition-all duration-300 ${
                  serviceType === type
                    ? 'border-primary bg-primary/10'
                    : 'border-border bg-background hover:border-primary/50'
                }`}
              >
                <div className="text-sm font-body font-semibold text-textPrimary capitalize">
                  {type === 'standard' && 'Standard'}
                  {type === 'express' && 'Ekspres'}
                  {type === 'premium' && 'Premium'}
                  {type === 'delicate' && 'Delicate'}
                </div>
                <div className="text-xs text-textSecondary mt-1">RM{price.toFixed(2)}/kg</div>
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-body font-semibold text-textPrimary mb-3">
            Servis Tambahan
          </label>
          <div className="space-y-2">
            {Object.entries(addOnPrices).map(([addOn, price]) => (
              <button
                key={addOn}
                onClick={() => handleAddOnToggle(addOn)}
                className={`w-full flex items-center justify-between p-3 rounded-lg border-2 transition-all duration-300 ${
                  addOns.includes(addOn)
                    ? 'border-primary bg-primary/10'
                    : 'border-border bg-background hover:border-primary/50'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                      addOns.includes(addOn) ? 'border-primary bg-primary' : 'border-border'
                    }`}
                  >
                    {addOns.includes(addOn) && (
                      <Icon name="CheckIcon" size={16} variant="outline" className="text-white" />
                    )}
                  </div>
                  <span className="text-sm font-body text-textPrimary capitalize">
                    {addOn === 'ironing' && 'Seterika'}
                    {addOn === 'folding' && 'Lipat Kemas'}
                    {addOn === 'delivery' && 'Penghantaran'}
                    {addOn === 'stainRemoval' && 'Buang Kesan'}
                  </span>
                </div>
                <span className="text-sm font-body font-semibold text-textSecondary">
                  +RM{price.toFixed(2)}/kg
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-lg p-6 border border-primary/20">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-textSecondary font-body mb-1">Anggaran Jumlah</div>
              <div className="text-3xl font-heading font-bold text-primary">
                RM{calculateTotal().toFixed(2)}
              </div>
            </div>
            <Icon
              name="CurrencyDollarIcon"
              size={48}
              variant="outline"
              className="text-primary opacity-50"
            />
          </div>
          <div className="mt-4 text-xs text-textSecondary font-body">
            * Harga adalah anggaran. Harga sebenar mungkin berbeza bergantung kepada agent dan
            kawasan.
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingCalculator;
